//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';

/**
 * @service PriceTickerUploadService
 * @description Service for managing the upload and status tracking of market price ticker files.
 * This service facilitates the bulk upload of market price data via Excel files and allows
 * retrieval of the status of previously uploaded files.
 *
 * It interacts with the `/blubay/` backend endpoint, specifically targeting:
 * - `marketpricefilestatus/`: To fetch the history of uploaded files.
 * - `marketprice/uploadMarketPrice`: To submit new files for processing.
 *
 * **Key Responsibilities:**
 * 1. **File Upload**: Sending metadata and file paths for market price Excel sheets to the backend.
 * 2. **Status Retrieval**: Fetching a list of uploaded files to track processing status.
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Upload market price Excel files.
 * @feature 2. View history of uploaded files.
 * @feature 3. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<any> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /blubay/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class PriceTickerUploadService extends Service<any> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/blubay/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/');
  }

  /**
   * @method getUploadedFiles
   * @async
   * @description Fetch the list of uploaded market price files and their statuses.
   *
   * Retrieves a large dataset (pageSize=50000) of file status records, ordered by creation date ('new').
   *
   * @returns {Promise<any>} Promise resolving to the list of uploaded file statuses.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request to `marketpricefilestatus/`.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * priceTickerUploadService.getUploadedFiles().then(files => {
   *   console.log('Uploaded Files:', files);
   * });
   */
  public getUploadedFiles() {
    return new Promise<any>((resolve, reject) => {
      const request = this.httpClient.get<any>(`${this.baseUrl}${this.serviceUrl}marketpricefilestatus/?pageSize=50000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method sendFileToUpload
   * @async
   * @description Submit a market price file for upload and processing.
   *
   * Sends a POST request with file metadata and the path to the Excel file (likely stored temporarily or on S3).
   *
   * @param {string} filename - The name of the file being uploaded.
   * @param {string} createdBy - The ID or name of the user creating the upload.
   * @param {string} updatedBy - The ID or name of the user updating the record.
   * @param {any} excelFilepath - The path or reference to the Excel file content.
   * @returns {Promise<any>} Promise resolving to the upload response.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `marketprice/uploadMarketPrice`.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * priceTickerUploadService.sendFileToUpload('prices.xlsx', 'user1', 'user1', '/tmp/path').then(res => {
   *   console.log('Upload initiated');
   * });
   */
  public sendFileToUpload(filename: string, createdBy: string, updatedBy: string, excelFilepath: any) {
    return new Promise<any>((resolve, reject) => {
      const request = this.httpClient.post<any>(`${this.baseUrl}${this.serviceUrl}marketprice/uploadMarketPrice`,
      {
        "fileName": filename,
        "created_by": createdBy,
        "updated_by": updatedBy,
        "excelPath": excelFilepath
      }
      , this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
